import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { ValidationResult } from 'src/easyclaim.api.client.typescript/src';
import { IconError } from 'src/easyclaim.app.assets';
import { SolvdCamera } from 'src/easyclaim.app.assets/icons/SolvdCamera';
import { SolvdCheckCircle } from 'src/easyclaim.app.assets/icons/SolvdCheckCircle';
import { SolvdWarning } from 'src/easyclaim.app.assets/icons/SolvdWarning';
import { useValidation } from 'src/easyclaim.app.contexts/ValidationContextCore';

import styles from './styles/ValidationContainer.module.css';

const validationConfig = {
	[ValidationResult.Warning]: {
		icon: <SolvdWarning />,
		firstButtonContent: <FormattedMessage id="button.changeImage" />,
		secondButtonContent: <FormattedMessage id="button.acceptImage" />,
		hideSecondButton: false,
		containerClass: styles.containerWarning,
	},
	[ValidationResult.Error]: {
		icon: <IconError />,
		firstButtonContent: <FormattedMessage id="button.anotherImage" />,
		secondButtonContent: null,
		hideSecondButton: true,
		containerClass: styles.containerError,
	},
	[ValidationResult.Correct]: {
		icon: <SolvdCheckCircle />,
		firstButtonContent: null,
		secondButtonContent: null,
		hideSecondButton: true,
		containerClass: styles.containerCorrect,
	},
};

interface FeedbackItem {
	title?: string | null;
	description?: string | null;
	validationResult?: ValidationResult;
}

interface ValidationContainerProps {
	validationId: string;
	validationResult: ValidationResult;
	blockingValidationResult?: ValidationResult;
	hideChangeImage?: boolean;
	hideAcceptImage?: boolean;
	hasWarnings: boolean | null | undefined;
	feedbackItems?: FeedbackItem[];
	additionalFeedback?: React.ReactNode;
	onChangeImage?: () => void;
	onAcceptImage?: () => void;
}

export const ValidationContainer = ({
	validationId,
	validationResult,
	blockingValidationResult,
	hideChangeImage,
	hideAcceptImage,
	hasWarnings,
	feedbackItems,
	additionalFeedback,
	onChangeImage,
	onAcceptImage,
}: ValidationContainerProps) => {
	const { getAccepted, setAccepted, setValidationResult, unregister } = useValidation();

	const accepted = getAccepted(validationId);

	useEffect(() => {
		setValidationResult(validationId, blockingValidationResult ?? validationResult);
	}, [validationId, validationResult, blockingValidationResult, setValidationResult]);

	useEffect(() => {
		return () => unregister(validationId);
	}, [validationId]);

	const currentResult = accepted ? ValidationResult.Correct : validationResult;
	const config = validationConfig[currentResult];

	if (!config) return null;

	const isContentAndButtonsVisible = currentResult !== ValidationResult.Correct;

	const handleAcceptImage = () => {
		setAccepted(validationId, true);
		if (onAcceptImage) onAcceptImage();
	};

	const handleChangeImage = () => {
		if (onChangeImage) onChangeImage();
	};

	return (
		<div
			className={`validation-container ${currentResult} ${styles.container} ${config.containerClass}`}
		>
			<div className={styles.header}>
				{config.icon}
				{currentResult === ValidationResult.Correct ? (
					<span>
						<FormattedMessage id="validationContainer.correct" />
					</span>
				) : (
					<div>
						{feedbackItems?.map((item, index) => (
							<div key={index} className={styles.validationTitle}>
								{item.validationResult !== ValidationResult.Correct && item.title && (
									<span>{item.title}</span>
								)}
							</div>
						))}
					</div>
				)}
			</div>
			{isContentAndButtonsVisible && (
				<>
					<div className={styles.content}>
						<>
							<div>
								{feedbackItems?.map((item, index) => (
									<div key={index} className={styles.hint}>
										{item.description && (
											<p dangerouslySetInnerHTML={{ __html: item.description }} />
										)}
									</div>
								))}
							</div>
							{hasWarnings && additionalFeedback}
						</>
					</div>
					<div className={styles.buttonsContainer}>
						{!hideChangeImage &&
							!validationId.endsWith('_mileage') &&
							config.firstButtonContent && (
								<button onClick={handleChangeImage}>
									<SolvdCamera />
									{config.firstButtonContent}
								</button>
							)}
						{!hideAcceptImage && !config.hideSecondButton && config.secondButtonContent && (
							<button onClick={handleAcceptImage}>
								{!validationId.endsWith('_mileage') ? (
									config.secondButtonContent
								) : (
									<FormattedMessage id="button.acceptMileage" />
								)}
							</button>
						)}
					</div>
				</>
			)}
		</div>
	);
};
